<template>
  <div class="shopping-chart-container">
    <!-- Product List Section -->
    <div class="products-section">
      <!-- Open Table Alert - placed at top before sale lines -->
      <div v-if="showOpenTableAlert" class="open-table-alert">
        <font-awesome-icon :icon="['fas', 'utensils']" class="me-2" />
        Open Table: {{ openTableName }}
      </div>

      <div class="sale-details-section" ref="saleLinesTable">
        <div class="shopping-cart" ref="shoppingCart">
          <ul class="list-group list-group-flush lists">
            <li class="list-group-item sale-line-item" 
                :key="sale.id || sale.index || `${sale.Plu}-${sale.Description}-${index}`"
                v-for="(sale, index) in currentSaleLines" 
                :class="{
                  'new-item': isLineNew(sale, index),
                  'updated-item': isLineUpdated(sale, index),
                  'old-item': !isLineNew(sale, index) && !isLineUpdated(sale, index),
                  'selected': selectedLine === index
                }">
              <div class="sale-line-content">
                <input type="checkbox" 
                       class="sale-checkbox" 
                       :checked="selectedLine === index" 
                       @change="setLine(index)" />
                <NewCurrentSaleLine
                  @click="setLine(index)"
                  @open-discount-modal="handleOpenDiscountModal"
                  :sale-line="sale"
                  :is-new-item="isLineNew(sale, index)"
                  :is-updated-item="isLineUpdated(sale, index)"
                  :ref="el => setLineRef(el, index)" />
              </div>
            </li>
          </ul>
        </div>
      </div>
      <SaleDetails class="sales" />
    </div>

    <!-- Floating Control Buttons -->
    <div class="floating-controls">
      <!-- Main Action Buttons -->
      <div class="main-buttons">
        <button class="floating-btn" :disabled="!currentSaleLines.length" @click="previousSelectedLine" title="Previous Item">
          <font-awesome-icon :icon="['fas', 'chevron-up']" />
        </button>
        <button class="floating-btn" :disabled="!currentSaleLines.length" @click="nextSelectedLine" title="Next Item">
          <font-awesome-icon :icon="['fas', 'chevron-down']" />
        </button>

        <button class="floating-btn" :disabled="selectedLine === null" @click="addToCart" title="Add Quantity">
          <font-awesome-icon :icon="['fas', 'plus']" />
        </button>
        <button class="floating-btn" :disabled="selectedLine === null" @click="removeFromCart" title="Reduce Quantity">
          <font-awesome-icon :icon="['fas', 'minus']" />
        </button>

        <button class="floating-btn" :disabled="selectedLine === null" @click="removeItem" title="Void Item">
          <font-awesome-icon :icon="['fas', 'trash']" />
        </button>
        <button class="floating-btn" :disabled="selectedLine === null" @click="showDiscount" title="Apply Discount">
          <font-awesome-icon :icon="['fas', 'percent']" />
        </button>
        <button class="floating-btn" :disabled="selectedLine === null" @click="qty" title="Set Quantity">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>

      <!-- Speed Dial for Additional Options -->
      <div class="speed-dial">
        <button class="floating-btn speed-dial-trigger" @click="toggleSpeedDial" title="More Options">
          <font-awesome-icon :icon="['fas', speedDialOpen ? 'chevron-right' : 'chevron-left']" />
        </button>

      <div class="speed-dial-buttons" v-if="speedDialOpen">
        <button class="floating-btn" @click="syncData" title="Refresh">
          <font-awesome-icon :icon="['fas', 'arrows-rotate']" />
        </button>
        <button class="floating-btn" @click="toggleTheme" title="Toggle Theme">
            <font-awesome-icon :icon="['fas', currentTheme === 'dark' ? 'sun' : 'moon']" />
          </button>
          <button class="floating-btn" v-if="customer" @click="unselectCustomer" title="Remove Customer">
            <font-awesome-icon :icon="['fas', 'user-minus']" />
          </button>
          <button class="floating-btn" v-else @click="selectCustomer" title="Add Customer">
            <font-awesome-icon :icon="['fas', 'user-plus']" />
          </button>
        </div>
      </div>
    </div>

    <!-- Offline Create Customer Modal -->
    <custom-modal ref="offlineCustomerModal" title="Create Customer (Offline)" :is-form="true" @submit="submitOfflineCustomer">
      <div class="form-floating mb-2">
        <input type="text" class="form-control" id="offline_name" v-model="offlineCustomerForm.Name" placeholder="Customer Name" required>
        <label for="offline_name">Customer Name</label>
      </div>
      <div class="form-floating mb-2">
        <input type="tel" class="form-control" id="offline_contact" v-model="offlineCustomerForm.CellNumber" placeholder="Contact No." required>
        <label for="offline_contact">Contact No.</label>
      </div>
      <div class="form-floating mb-2">
        <input type="text" class="form-control" id="offline_surname" v-model="offlineCustomerForm.Surname" placeholder="Surname (optional)">
        <label for="offline_surname">Surname (optional)</label>
      </div>
      <div class="form-floating mb-2">
        <input type="text" class="form-control" id="offline_address" v-model="offlineCustomerForm.Adress1" placeholder="Address (optional)">
        <label for="offline_address">Address (optional)</label>
      </div>
    </custom-modal>
    
    <div class="modal fade" id="exampleModal" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="exampleModalLabel">Voucher Purchasing Screen</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" style="max-height: 75vh; overflow-y: auto;">
            <div class="row">
              <!-- Left side: existing design -->
              <div class="col-md-4">
                <div class="list-group">
                  <a class="list-group-item list-group-item-action" v-for="(option, index) in options" :key="index"
                    @click="selectOption(option)">
                    {{ option }}
                  </a>
                </div>
              </div>
              <!-- Right side: list of other clickable options -->
              <div class="col-md-8">
                <div class="product-card" v-for="item in selectedItem" :key="item.PLU" @click="checkItemOffer(item)">
                  <div class="stock-card_img mx-2 mt-2" v-if="item.Image">
                    <img :src="item.Image" alt="Item Image">
                  </div>
                  <div class="card-body mx-3 p-1 d-flex justify-content-between flex-column">
                    <span class="text-secondary text-sm">Stock Code : {{ item.PLU }}</span>
                    <h6 class="product-description">
                      {{ item.Description }} <br>
                      <span class="product-description2">{{ item.Description2 }}</span>
                    </h6>
                    <h5 class="product-price">{{ $utils.formatMoney(item.SellingIncl) }}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" id="closebutton" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
    <!--        modal ends for voucher-->

    <!-- Discount Modal -->
    <div class="modal fade" id="discount-modal" tabindex="-1" aria-labelledby="discount-modal-label" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="discount-modal-label">
              Apply Discount to {{ $utils.toPascalCase($utils.cutLength(discountModalData?.saleLine?.Description || '', 30)) }}
            </h5>
            <button type="button" class="btn-close" @click="closeDiscountModal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="applyDiscount">
            <div class="d-flex flex-row p-1 justify-content-evenly mt-2">
              <label>
                <input class="form-check-input" type="radio" value="value" v-model="discountType">
                Value Discount
              </label>
              <label>
                <input class="form-check-input" type="radio" value="percent" v-model="discountType">
                Percentage Discount
              </label>
            </div>
            <hr class="mx-2"/>
            <label class="row mx-3 mb-2">
              Discount Amount
              <input type="number" class="form-control" v-model="discountAmount">
            </label>
            <div class="modal-footer mt-3">
              <button type="button" class="btn btn-secondary" @click="closeDiscountModal">Close</button>
              <button type="submit" class="btn btn-primary">Save changes</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SaleDetails from "@/components/point-of-sale/sales/SaleDetails.vue";
import NewCurrentSaleLine from "@/components/point-of-sale/sales/NewCurrentSaleLine.vue";
import CustomModal from "@/components/layout/utils/CustomModal";
import { Storage } from "@/services/Storage";
import store from "@/store";
import {toggleTheme} from "@/assets/js/theme";
import connectivityMonitor from "@/services/connectivityMonitor";
import { responses } from "@/utils/responses";
import { errorHandler } from "@/services/errorHandler";
import * as bootstrap from 'bootstrap';
// docs/FRONTEND.md — sale line Qty contract; qty-reset-on-save fix
// Reference: MobileCartView.incrementQuantity → updateSaleLineQty (sales-module ~L807)
import { nextInPlaceQty } from "@/utils/saleLineQty";


export default {
  name: "ShoppingChart",
  components: { NewCurrentSaleLine, SaleDetails, CustomModal },
  computed: {
    openTableName() {
      return (this.$store.getters.getOpenTableName || '').toString().trim();
    },
    hasOpenTable() {
      return this.openTableName.length > 0;
    },
    showOpenTableAlert() {
      return this.hasOpenTable;
    },
    options() {
      return this.$store.getters.getZedMobileCategories;
    },
    customer() {
      let id = this.$store.getters.getCustomer;
      return this.$store.getters.getDebtorByID(id);
    },
    currentSaleLines() {
      return this.$store.getters.getSaleLines
    },
  },
  data() {
    return {
      speedDialOpen: false,
      selectedLine: null,
      lineRefs: {},
      initialLineCount: 0,
      originalLines: [],
      selectedItem: [],
      currentTheme: localStorage.getItem('theme') || 'light',
      isOnline: true,
      unsubscribeOnline: null,
      unsubscribeOffline: null,
      offlineCustomerForm: { Name: '', CellNumber: '', Surname: '', Adress1: '', Area: '', vat: '' },
      // Discount modal data
      discountModalData: null,
      discountType: 'value',
      discountAmount: 0.00
    }
  },
  watch: {
    currentSaleLines(newLines, oldLines) {
      // Reset the count if the newLines array is refreshed
      if (newLines.length !== oldLines.length) {
        this.setInitialLineCount();
      }
    }
  },
  created() {
    // Set initial line count when the component is created
    this.setInitialLineCount();
  },
  mounted() {
    // Initialize online status
    this.isOnline = connectivityMonitor.getStatus();
    // Subscribe to connectivity events
    this.unsubscribeOnline = connectivityMonitor.subscribe('online', () => {
      this.isOnline = true;
    });
    this.unsubscribeOffline = connectivityMonitor.subscribe('offline', () => {
      this.isOnline = false;
    });
  },
  beforeDestroy() {
    if (this.unsubscribeOnline) this.unsubscribeOnline();
    if (this.unsubscribeOffline) this.unsubscribeOffline();
  },
  methods: {
    toggleTheme() {
      this.currentTheme = toggleTheme();
    },
    checkItemOffer(item) {
      this.currentItem = item; // Track the selected item
      const hasOffer = item.offer;

      if (hasOffer) {
        this.triggerModal('openAmountInputModalButton');
      } else {
        this.triggerModal('openCustomerInformationModalButton');
      }
    },
    triggerModal(buttonId) {
      const button = document.getElementById(buttonId);
      if (button) {
        button.click();
      }
    },
    selectOption(option) {
      this.$utils.showLoading();
      this.selectedCategory = option;
      return axios.get(zedMobileApi + 'products/getByCatgeory', {
        params: { category: option },
        headers: { "Api-key": this.$store.getters.getZedMobileApiKey }
      }).then(response => this.selectedItem = response.data)
        .catch(error => errorHandler.tomcatError(error))
        .finally(() => this.$utils.hideLoading());
    },
    selectCustomer() {
      // If online, keep existing behavior: open select-customer flow
      if (this.isOnline) {
        return this.$router.push({ name: `select-customer`, query: { saleNumber: this.$route.query.saleNumber } })
      }
      // Offline: open inline modal to create a quick customer and attach to sale
      if (this.$refs.offlineCustomerModal) {
        this.offlineCustomerForm = { Name: '', CellNumber: '', Surname: '', Adress1: '', Area: '', vat: '' };
        this.$refs.offlineCustomerModal.openModal();
      }
    },
    async submitOfflineCustomer() {
      try {
        if (!this.offlineCustomerForm?.Name || !this.offlineCustomerForm?.CellNumber) {
          responses.showWarning('Please provide Name and Contact');
          return;
        }
        this.$utils.showLoading();
        const rec = await this.$store.dispatch('createOfflineDebtor', this.offlineCustomerForm);
        if (rec && rec.Id) {
          this.$store.commit('setCustomer', rec.Id);
          responses.showSuccess('Offline customer added to sale');
          if (this.$refs.offlineCustomerModal) this.$refs.offlineCustomerModal.closeModal();
        }
      } catch (e) {
        errorHandler.tomcatError(e, false, 'Failed to save offline customer');
      } finally {
        this.$utils.hideLoading();
      }
    },
    unselectCustomer() {
      this.$store.commit(`setCustomer`, '')
    },
    toggleSpeedDial() {
      this.speedDialOpen = !this.speedDialOpen;
    },
    qty() {
      if (this.selectedLine !== null) {
        return this.$store.dispatch('validateSecurityPermissionNoPrompt', 'manualWeightEntry')
          .then(async (canManualWeightEntry) => {
            const selectedSaleLine = this.currentSaleLines[this.selectedLine];
            const set = await this.$store.dispatch('getStockSetting', selectedSaleLine?.Plu);
            const isScaleItem = Boolean(set?.scale || set?.Scale);

            if (isScaleItem && !canManualWeightEntry) {
              responses.showWarning('Manual quantity entry is disabled for scale items on your account. Use Read Scale instead.');
              return;
            }

            return Swal.fire({
              title: 'Set Quantity',
              icon: 'question',
              input: 'text',
              showCancelButton: true,
              confirmButtonText: 'Submit',
              showLoaderOnConfirm: true,
              allowOutsideClick: false,
              inputValidator: async (input) => {
                if (!input || isNaN(input) || input <= 0) {
                  return 'Please enter a valid number greater than 0';
                }
                this.lineRefs[this.selectedLine].saleLine.Qty = Number(input);
                await this.$store.dispatch(`recalculateVat`)
                this.$store.dispatch(`calculateTipAmount`)
                localStorage.setItem('currentSaleLines', JSON.stringify(this.$store.getters.getSaleLines));
              },
            });
          });
      } else {
        responses.showWarning('No line selected');
        console.warn('No line selected');
      }
    },
    isLineNew(saleLine, index) {
      // Check if the line is added after the initial table load
      return index >= this.initialLineCount;
    },
    isLineUpdated(saleLine, index) {
      if (index >= this.originalLines.length) return false;

      const originalLine = this.originalLines[index];
      return this.hasLineChanged(originalLine, saleLine);
    },
    hasLineChanged(originalLine, currentLine) {
      // Define what constitutes a change
      const keysToCompare = [
        'Qty',
        'Unit',
        'Discount',
        'Line',
        'instructions'
      ];

      return keysToCompare.some(key => {
        if (key === 'instructions') {
          return JSON.stringify(originalLine.instructions) !== JSON.stringify(currentLine.instructions);
        }
        return originalLine[key] !== currentLine[key];
      });
    },
    setInitialLineCount() {
      if (this.initialLineCount === 0) {
        this.initialLineCount = this.currentSaleLines.length;
        this.originalLines = JSON.parse(JSON.stringify(this.currentSaleLines));
      }
    },
    syncData() {
      return window.location.reload(true);
    },
    setLine(index) {
      // If clicking the same line again, deselect it
      if (this.selectedLine === index) {
        this.selectedLine = null;
      } else {
        // Select the new line
        this.selectedLine = index;
      }
    },
    process() {
      return this.$router.push({ name: 'payment' })
    },
    askForPrice(item) {
      this.$store.commit('setIsTabOpen', true)
      return Swal.fire({
        allowOutsideClick: false,
        showCancelButton: true,
        showLoaderOnConfirm: true,
        title: `Set Selling Price`,
        html: `<h6 >Description : ${item.Description}</h6>
               <h6 >Current Price : ${$utils.formatMoney(item.SellingIncl)}</h6>
               <input type="number" step="0.01" id="selling-prc" class="swal2-input" placeholder="Selling Price" style="max-width: 70%">`,
        confirmButtonText: 'Set Price',
        focusConfirm: false,
        preConfirm: async () => {
          let value = Swal.getPopup().querySelector('#selling-prc').value;
          if (!value) {
            Swal.showValidationMessage(`Please enter a valid Selling Price`)
          }
          return parseFloat(value);
        }
      }).then(result => {
        this.$store.commit('setIsTabOpen', false)
        if (result.isConfirmed) {
          return result.value;
        }
        return parseFloat(item.SellingIncl.toFixed(2))
      })
    },
    askForDescription(item) {
      this.$store.commit('setIsTabOpen', true)
      return Swal.fire({
        allowOutsideClick: false,
        showCancelButton: true,
        showLoaderOnConfirm: true,
        title: `Set Description`,
        html: `<h6 >Description : ${item.Description}</h6>
               <textarea id="desc-ask" class="swal2-input" placeholder="Description" style="max-width: 70%">`,
        confirmButtonText: 'Set Description',
        focusConfirm: false,
        preConfirm: async () => {
          let value = Swal.getPopup().querySelector('#desc-ask').value
          if (!value || value.toString().trim() === "") {
            Swal.showValidationMessage(`Please enter a valid Description`)
          }
          return value;
        }
      }).then(result => {
        this.$store.commit('setIsTabOpen', false)
        if (result.isConfirmed) {
          return result.value;
        }
        return item.Description
      })
    },
    previousSelectedLine() {
      if (this.selectedLine !== null) {
        if (this.selectedLine > 0) {
          this.selectedLine--;
        } else {
          this.selectedLine = this.currentSaleLines.length - 1;  // Wrap around to the last item
        }
      } else {
        responses.showWarning('No line selected')
        console.warn("No line selected");
      }
    },
    nextSelectedLine() {
      if (this.selectedLine !== null) {
        if (this.selectedLine < this.currentSaleLines.length - 1) {
          this.selectedLine++;
        } else {
          this.selectedLine = 0;  // Wrap around to the first item
        }
      } else {
        responses.showWarning('No line selected')
        console.warn("No line selected");
      }
    },
    removeFromCart() {
      if (this.selectedLine !== null) {
        this.$store.commit(`setOverrideDetails`, `Void Item (${this.currentSaleLines[this.selectedLine].Description})`)
        return this.$store.commit("voidItem", this.currentSaleLines[this.selectedLine]);
      } else {
        responses.showWarning('No line selected')
        console.warn("No line selected");
      }
    },
    /**
     * Floating + control: in-place +1 on the selected cart line via
     * updateSaleLineQty (sales-module ~L807). Matches MobileCartView.
     *
     * Previously re-committed addToCurrentSaleLines with Qty:1, which is
     * qty-reset-on-save loss vector #2. Cooking-link PLUs still open the
     * instruction picker so modifiers can be attached as a separate line.
     *
     * @returns {Promise<*>|void}
     */
    addToCart() {
      if (this.selectedLine !== null) {
        const saleLine = this.currentSaleLines[this.selectedLine];
        const plu = saleLine.Plu;
        return this.$store.dispatch('getStockSetting', plu)
          .then(async (set) => {
            let cookingLink = 0;
            if (set) {
              cookingLink = set.cookingLink;
            }

            if (cookingLink) {
              return this.$router.push({
                name: 'instruction',
                query: {
                  plu: saleLine.Plu
                }
              });
            }

            // In-place +1 — never re-add with Qty:1
            const newQty = nextInPlaceQty(saleLine);
            this.$store.commit('updateSaleLineQty', {
              saleLine,
              qty: newQty,
            });
            return this.recalculateTip();
          })
      } else {
        responses.showWarning('No line selected')
        console.warn("No line selected");
      }
    },
    recalculateTip() {
      return this.$store.dispatch('calculateTipAmount')
    },
    setLineRef(el, index) {
      // Dynamic ref setting
      if (el) {
        this.lineRefs[index] = el;
      }
    },
    showDiscount() {
      if (this.selectedLine !== null) {
        const selectedLineComponent = this.lineRefs[this.selectedLine];

        if (selectedLineComponent && selectedLineComponent.discount) {
          selectedLineComponent.discount();
        } else {
          console.error("Discount method not found for the selected line.");
        }
      } else {
        responses.showWarning('No line selected')
        console.warn("No line selected");
      }
    },
    handleOpenDiscountModal(data) {
      this.discountModalData = data;
      this.discountType = 'value';
      this.discountAmount = 0.00;
      this.$nextTick(() => {
        const modalEl = document.getElementById('discount-modal');
        if (modalEl) {
          const modal = new bootstrap.Modal(modalEl);
          modal.show();
        }
      });
    },
    closeDiscountModal() {
      const modalEl = document.getElementById('discount-modal');
      if (modalEl) {
        const modal = bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
      }
    },
    applyDiscount() {
      if (!this.discountModalData || !this.discountModalData.saleLine) return;
      let type = this.discountType;
      let saleLine = this.discountModalData.saleLine;
      let line = Number(saleLine.Unit) * Number(saleLine.Qty);
      let discount = Number(this.discountAmount);
      if (type === 'percent') discount = (this.discountAmount / 100) * line;
      saleLine.Discount = discount;
      saleLine.Line = Number(saleLine.Line) - discount;
      this.closeDiscountModal();
    },
    async removeItem() {
      if (this.selectedLine !== null) {
        const selectedLineComponent = this.lineRefs[this.selectedLine];
        if (selectedLineComponent && selectedLineComponent.voidItem) {
          const item = this.currentSaleLines[this.selectedLine];
          selectedLineComponent.voidItem(item);
        } else {
          console.error("void method not found for the selected line.");
        }
      } else {
        responses.showWarning('No line selected')
        console.warn("No line selected");
      }
    }
  },


}
</script>

<style lang="scss" scoped>
// Main Container
.shopping-chart-container {
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  gap: 0;
  background: var(--bg-primary);
  position: relative;
}

// Products Section
.products-section {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--card-bg);
}

.sale-details-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px 0 0 0;
  box-shadow: var(--card-shadow);
}

.shopping-cart {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

// Sale Lines Styling
.lists {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  margin: 0;
  
  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--secondary-button-bg);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(var(--accent-primary-rgb), 0.3);
    border-radius: 4px;
    
    &:hover {
      background: rgba(var(--accent-primary-rgb), 0.5);
    }
  }
}

.sale-line-item {
  transition: all 0.3s ease;
  background: var(--card-bg);
  
  &:hover {
    box-shadow: var(--card-shadow-hover);
    transform: translateX(2px);
  }

  &.selected {
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 2px rgba(var(--accent-primary-rgb), 0.2);
    background: rgba(var(--accent-primary-rgb), 0.05);
  }
}

.sale-line-content {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.sale-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--accent-primary);
}

// Item Status Styling
.new-item {
  background: linear-gradient(135deg, rgba(46, 204, 113, 0.12) 0%, rgba(46, 204, 113, 0.06) 100%);
  border-left: 4px solid #2ecc71 !important;
  animation: slideInNew 0.4s ease-out;
}

@keyframes slideInNew {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.updated-item {
  background: linear-gradient(135deg, rgba(241, 196, 15, 0.15) 0%, rgba(241, 196, 15, 0.08) 100%);
  border-left: 4px solid #f1c40f !important;
  animation: pulse 0.5s ease-in-out;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

.old-item {
  background-color: transparent;
  border-left: 4px solid transparent !important;
}

// Open Table Alert
.open-table-alert {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  margin: 12px 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
}

// Floating Controls
.floating-controls {
  position: absolute;
  bottom: 5%;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 800;
  
  // Responsive adjustments for zoom levels
  @media (max-height: 800px), (min-resolution: 1.1dppx) {
    gap: 8px;
    right: 8px;
    bottom: 4%;
  }
  
  @media (max-height: 700px), (min-resolution: 1.25dppx) {
    gap: 6px;
    right: 6px;
    bottom: 3%;
  }
  
  @media (max-height: 600px), (min-resolution: 1.5dppx) {
    gap: 5px;
    right: 4px;
    bottom: 2%;
  }
}

.main-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
  
  @media (max-height: 700px), (min-resolution: 1.25dppx) {
    gap: 5px;
  }
  
  @media (max-height: 600px), (min-resolution: 1.5dppx) {
    gap: 4px;
  }
}

.speed-dial {
  position: relative;
}

.speed-dial-buttons {
  position: absolute;
  bottom: 0;
  right: 50px;
  display: flex;
  flex-direction: row;
  gap: 6px;
  background: var(--card-bg);
  border-radius: 10px;
  padding: 10px;
  box-shadow: var(--card-shadow);
  animation: slideInLeft 0.2s ease-in-out;
  
  @media (max-height: 700px), (min-resolution: 1.25dppx) {
    right: 45px;
    padding: 8px;
    gap: 5px;
  }
  
  @media (max-height: 600px), (min-resolution: 1.5dppx) {
    right: 40px;
    padding: 6px;
    gap: 4px;
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.floating-btn {
  background: var(--primary-button-bg);
  color: var(--primary-button-text);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--card-shadow);
  cursor: pointer;
  transition: transform 0.2s ease-in-out, background 0.2s ease-in-out;
  font-size: 0.85rem;
  flex-shrink: 0;
  
  // Responsive sizing for zoom levels
  @media (max-height: 800px), (min-resolution: 1.1dppx) {
    width: 33px;
    height: 33px;
    font-size: 0.8rem;
  }
  
  @media (max-height: 700px), (min-resolution: 1.25dppx) {
    width: 30px;
    height: 30px;
    font-size: 0.75rem;
  }
  
  @media (max-height: 600px), (min-resolution: 1.5dppx) {
    width: 28px;
    height: 28px;
    font-size: 0.7rem;
  }
  
  @media (max-height: 500px), (min-resolution: 1.75dppx) {
    width: 26px;
    height: 26px;
    font-size: 0.65rem;
  }

  &:hover:not(:disabled) {
    transform: scale(1.1);
    background: var(--primary-button-hover-bg);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.speed-dial-trigger {
    background: var(--accent-electric-blue);
    color: white;
    
    &:hover {
      background: var(--accent-neon-purple);
    }
  }
}

.sales {
  width: 28%;
  position: absolute !important;
  bottom: 5px !important;
}

.lists {
  cursor: pointer;
  position: relative;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  
  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(var(--accent-primary-rgb), 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(var(--accent-primary-rgb), 0.5);
    }
  }
  
  .list-group-item {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    margin-bottom: 6px;
    transition: all 0.3s ease;
    
    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transform: translateX(2px);
    }
  }
}

.new-item {
  background: linear-gradient(135deg, rgba(39, 174, 96, 0.15) 0%, rgba(39, 174, 96, 0.08) 100%);
  border-left: 4px solid #27ae60 !important;
  animation: slideInNew 0.4s ease-out;
}

@keyframes slideInNew {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.updated-item {
  background: linear-gradient(135deg, rgba(241, 196, 15, 0.2) 0%, rgba(241, 196, 15, 0.1) 100%);
  border-left: 4px solid #f1c40f !important;
  animation: pulse 0.5s ease-in-out;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

.old-item {
  background-color: transparent;
  border-left: 4px solid transparent !important;
}


#shopping-cart-panel {
  display: flex !important;
  flex-direction: column;
  border-left: none;
  padding: 0;
  position: relative;
  height: 100%;

  .scroll-controls {
    bottom: 0 !important;
    right: 0 !important;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
  }

  #customer-panel {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    height: 60px;
    border-bottom: 2px solid rgba(0, 0, 0, 0.08);
    padding: 8px 12px;
    background: linear-gradient(135deg, rgba(var(--accent-primary-rgb), 0.04) 0%, rgba(var(--accent-primary-rgb), 0.02) 100%);

    .select-customer-btn {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      color: var(--text-primary);
      font-size: 22px;
      background: linear-gradient(135deg, var(--card-bg) 0%, rgba(var(--accent-primary-rgb), 0.05) 100%);
      border: 2px solid rgba(var(--accent-primary-rgb), 0.2);
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      
      &:hover {
        transform: scale(1.05);
        border-color: var(--accent-primary);
        box-shadow: 0 4px 12px rgba(var(--accent-primary-rgb), 0.2);
      }
    }
  }

  .sale-details-section {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    overflow: hidden;

    .shopping-cart {
      flex: 1 1 auto;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 8px;
    }

    .sales-stats {
      flex: 0 1 auto;
      height: 85px;
      border-top: 2px solid rgba(0, 0, 0, 0.08);
      padding: 12px;
      position: absolute !important;
      bottom: 0 !important;
      left: 0;
      right: 0;
      background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, var(--card-bg) 100%);
      backdrop-filter: blur(10px);
      box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.05);

      .cart-actions {
        .btn {
          border-radius: 10px;
          color: #ffffff;
          font-weight: 700;
          border: none;
          padding: 10px 16px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
          }
          
          &:active {
            transform: translateY(0);
          }

          &.btn-primary {
            background: linear-gradient(135deg, #3496F0 0%, #2980b9 100%);
            box-shadow: 0 4px 12px rgba(52, 150, 240, 0.3);
            
            &:hover {
              box-shadow: 0 6px 20px rgba(52, 150, 240, 0.4);
            }
          }

          &.btn-secondary {
            background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
            box-shadow: 0 4px 12px rgba(149, 165, 166, 0.3);
            
            &:hover {
              box-shadow: 0 6px 20px rgba(149, 165, 166, 0.4);
            }
          }

          &.btn-dark {
            background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
            box-shadow: 0 4px 12px rgba(52, 73, 94, 0.3);
            
            &:hover {
              box-shadow: 0 6px 20px rgba(52, 73, 94, 0.4);
            }
          }
        }
      }
    }
  }
}

.sales-panel {
  display: flex;
  flex-direction: column;
  padding: 0;
  position: relative;

  .sale-header {
    background: #F2F2F4;
    box-shadow: 0 2px 8px 0 rgba(203, 153, 126, 0.4);
    border: 1px solid #E6E7EC;
    height: 53px;
    max-height: 55px;

    .header {
      display: flex;
      flex-direction: row;
      align-items: center;

      h2 {
        margin: 0;
      }

      button {
        width: 30px;
        background: white;
        font-size: 25px;
      }
    }
  }

  @media (min-height: 769px) {
    .sale-lines-table {
      height: 83vh !important;
    }
  }

  .sale-lines-table {
    flex: auto;
    overflow-y: auto;
    height: 77vh;

    .sales-list {
      list-style: none;
      padding: 0 0 130px 0;

      li {
        border: 1px solid #E6E7EC;
      }
    }
  }

  .sale-stats {
    position: absolute;
    background: #F2F2F4;
    border: 1px solid #E6E7EC;
    max-height: 163px;
    bottom: 0;
    left: 0;
    right: 0;
  }
}

.list-group-item {
  font-size: 1.2rem;
  /* Reduced font size */
  padding: 3px 5px;
  margin-right: 35px;
  /* Reduced padding */
}

h4,
h5,
h6 {
  margin: 0;
}

.example-card-img-responsive {
  width: 80px;
  height: 65px;
  margin: 10px;
  opacity: 0.8;
}

.product-card {
  height: 110px;
  transition: 200ms ease-in-out;
  overflow: hidden;
  width: 100%;
  background-color: var(--card-bg);
  border: var(--card-border);
  border-radius: var(--card-radius);
  display: flex;
  flex-direction: row;
  word-wrap: break-word;

  .product-description {
    font-weight: 600;
    color: var(--text-primary);
  }

  .product-description2 {
    font-weight: 400;
    color: var(--text-secondary);
  }

  .product-price {
    font-weight: 700;
    color: var(--text-primary);
  }

  &:hover,
  &:focus {
    background: var(--accent-primary);
    color: white;
    transform: var(--hover-scale);

    .product-description {
      color: white;
    }

    .product-price {
      color: white;
    }
  }
}

img {
  height: 94px;
  width: 94px;
  opacity: 0.7;
  border-radius: 10px;
}

.text-sm {
  font-size: 12px !important;
}
</style>
